import {
	ChangeDetectorRef,
	Component,
	forwardRef,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
} from '@angular/core';
import {
	ControlValueAccessor,
	FormBuilder,
	FormControl,
	FormsModule,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
} from '@angular/forms';
import { AsyncSubject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'ae-yes-no-checkbox-toggle',
	templateUrl: './yes-no-checkbox-toggle.component.html',
	styleUrls: ['./yes-no-checkbox-toggle.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => YesNoCheckboxToggleComponent),
			multi: true,
		},
	],
	imports: [FormsModule, ReactiveFormsModule],
})
export class YesNoCheckboxToggleComponent
	implements ControlValueAccessor, OnDestroy, OnInit
{
	@HostBinding('class.custom-form-control') customFormControl = true;
	private _unsubscribe$ = new AsyncSubject<null>();

	@Input() public allowNull: boolean = true;
	@Input() public disableYes: boolean = false;
	@Input() public disableNo: boolean = false;
	@Input() public yesTitle: string = 'Approve';
	@Input() public noTitle: string = 'Reject';

	protected checkedControl: FormControl<boolean | null>;

	protected yes: boolean;
	protected no: boolean;

	private _touchFunction: () => void;
	private _changeFunction: (value: boolean | null) => void = () => null;
	private _changeWatcher: Subscription;
	public disabled: boolean;

	constructor(
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}

	ngOnInit(): void {
		this.checkedControl = this.fb.control(false);

		// Creating a view safe value that can be checked similar to the native input checkbox
		this.checkedControl.valueChanges
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((v) => {
				setTimeout(() => {
					this.yes = v === true;
					this.no = v === false;
					this.cdr.markForCheck();
				});
			});

		this.cdr.detectChanges();
		this.watchForChange();
		this.cdr.detectChanges();
	}

	private watchForChange = () => {
		if (this._changeWatcher) return;

		this._changeWatcher = this.checkedControl.valueChanges
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((val) => {
				if (this._changeFunction) {
					this._changeFunction(val);
				}

				if (this._touchFunction) {
					this._touchFunction();
				}
			});
	};

	private stopWatchForChange = () => {
		this._changeWatcher?.unsubscribe();
		this._changeWatcher = null;
	};

	public writeValue(val: boolean = false): void {
		try {
			this.stopWatchForChange();
			this.checkedControl.setValue(val);
		} catch (e) {
			throw new Error(
				`YesNoCheckboxToggleComponent.writeValue could not set value`,
				{ cause: e },
			);
		} finally {
			this.watchForChange();
		}
	}

	public registerOnChange(fn: (value: unknown) => void): void {
		this._changeFunction = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this._touchFunction = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		this.stopWatchForChange();

		if (isDisabled) {
			this.checkedControl.disable();
		} else {
			this.checkedControl.enable();
		}

		this.watchForChange();
	}

	public changeValue = (val: 'yes' | 'no') => {
		if (this.checkedControl.enabled) {
			let hasChange = false;
			if (val === 'yes' && !this.disableYes && (this.allowNull || !this.yes)) {
				this.yes = !this.yes;
				this.no = false;
				hasChange = true;
			}
			if (val === 'no' && !this.disableNo && (this.allowNull || !this.no)) {
				this.no = !this.no;
				this.yes = false;
				hasChange = true;
			}
			if (hasChange) {
				this.checkedControl.setValue(this.yes ? true : this.no ? false : null);
			}
		}
	};
}
